import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import ThemeToggle from "../components/ThemeToggle";

function passwordChecks(pw) {
    return {
        length: pw.length >= 8,
        lower: /[a-z]/.test(pw),
        upper: /[A-Z]/.test(pw),
        digit: /\d/.test(pw),
    };
}

function Check({ ok, label, testid }) {
    return (
        <li
            data-testid={testid}
            className={`flex items-center gap-2 ${ok ? "text-emerald-700" : "text-ink-muted"}`}
        >
            <span
                aria-hidden
                className={`inline-block w-4 h-4 rounded-full border ${ok ? "bg-emerald-600 border-emerald-600" : "border-ink/30"}`}
            />
            {label}
        </li>
    );
}

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const checks = useMemo(() => passwordChecks(pw), [pw]);
    const matches = pw && pw === pw2;
    const allOk = checks.length && checks.lower && checks.upper && checks.digit && matches;

    const submit = async (e) => {
        e.preventDefault();
        if (!allOk) return;
        setError("");
        setBusy(true);
        try {
            await api.post("/auth/password-reset/confirm", { token, new_password: pw });
            setDone(true);
            setTimeout(() => navigate("/login", { replace: true }), 2400);
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-sand-50 text-ink flex items-center justify-center px-6">
                <div className="max-w-md text-center" data-testid="reset-missing-token">
                    <h1 className="font-serif text-3xl">Missing token</h1>
                    <p className="text-ink-soft mt-2">
                        This reset link is incomplete. Request a new one below.
                    </p>
                    <Link to="/forgot-password" className="sp-btn-primary inline-block mt-6">
                        Request a new link
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand-50 text-ink flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md">
                <div className="flex items-center justify-between">
                    <Link to="/login" className="sp-overline" data-testid="reset-back">← Sign in</Link>
                    <ThemeToggle />
                </div>
                <h1 className="text-3xl sm:text-4xl font-serif mt-6">Set a new password</h1>
                <p className="text-ink-soft mt-2">Choose something only you can guess.</p>

                {done ? (
                    <div className="mt-8 rounded-2xl border border-emerald-300 bg-emerald-50 p-6" data-testid="reset-success">
                        <div className="sp-overline text-emerald-800">Password updated</div>
                        <p className="mt-2 text-ink">
                            You can now sign in with your new password. Redirecting…
                        </p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-8 space-y-6">
                        <div>
                            <label className="sp-overline" htmlFor="new-pw">New password</label>
                            <input
                                id="new-pw"
                                type="password"
                                required
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                className="sp-input"
                                autoComplete="new-password"
                                data-testid="reset-password"
                            />
                        </div>
                        <div>
                            <label className="sp-overline" htmlFor="new-pw-2">Confirm new password</label>
                            <input
                                id="new-pw-2"
                                type="password"
                                required
                                value={pw2}
                                onChange={(e) => setPw2(e.target.value)}
                                className="sp-input"
                                autoComplete="new-password"
                                data-testid="reset-password-confirm"
                            />
                        </div>

                        <ul className="text-sm space-y-2" data-testid="reset-checks">
                            <Check ok={checks.length} label="At least 8 characters" testid="reset-check-length" />
                            <Check ok={checks.upper} label="1 uppercase letter" testid="reset-check-upper" />
                            <Check ok={checks.lower} label="1 lowercase letter" testid="reset-check-lower" />
                            <Check ok={checks.digit} label="1 number" testid="reset-check-digit" />
                            <Check ok={!!matches} label="Passwords match" testid="reset-check-match" />
                        </ul>

                        {error && <div className="text-sm text-red-700" data-testid="reset-error">{error}</div>}

                        <button
                            type="submit"
                            disabled={!allOk || busy}
                            className="sp-btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="reset-submit"
                        >
                            {busy ? "Updating…" : "Set new password"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
